import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'

// context
import LotContextProvider, { ILotContext } from './lot.context'

// types
import { Lot, OpeningHoursObject, Schedule } from '../../types/lot.interface'

// utils
import useParseOpeningHours from '../../utils/getOpeningHours.utils'
import { getCleanCityName } from '@utils/getCleanCityName.utils'

// graphql
import { GET_LOT_IMAGES_WITH_ID } from '../../graphql/queries/getLotImagesWithId.query'

import { BookingGetLotImageUrlsQuery, BookingGetLotImageUrlsQueryVariables } from 'parkdepot-shared/gql/graphql'
import { useLocation } from 'react-router-dom'

const updateLotObject = (newLot: Lot, openingHoursFn: (schedule: Schedule[]) => OpeningHoursObject[]) => {
    const address = newLot.address.split(', ')
    return {
        ...newLot,
        ...(!newLot.city && { address: address[0], city: address[1] }),
        ...(!newLot.cityName && { cityName: getCleanCityName(address[1]) }),
        openingHours: newLot.openingHours
            ? newLot.openingHours
            : openingHoursFn(newLot.opening_hours.opening_hours_rrules as Schedule[])
    }
}

export const LotContextContainer: React.FC = ({ children }) => {
    const location = useLocation()
    const [lot, setLot] = useState<Lot | null>(null)
    const [nextDisabled, setNextDisabled] = useState(true)
    const [nextButtonLoading, setNextButtonLoading] = useState(false)
    const { openingHours } = useParseOpeningHours()

    const [getLotImages] = useLazyQuery<BookingGetLotImageUrlsQuery, BookingGetLotImageUrlsQueryVariables>(
        GET_LOT_IMAGES_WITH_ID
    )

    const modifyAndSetLot = useCallback(
        (newLot: Lot) => {
            const updatedLot = updateLotObject(newLot, openingHours)
            setLot(updatedLot)
        },
        [openingHours]
    )

    const fetchImages = useCallback(async () => {
        if (!lot || lot.image_urls) return
        const images = await getLotImages({ variables: { lot_id: lot.id }, fetchPolicy: 'no-cache' })
        const primaryImage = images.data?.booking_get_lot_image_urls.primary_url || ''
        const secondaryImages = (images.data?.booking_get_lot_image_urls.secondary_urls || []) as string[]

        setLot({ ...lot, image_urls: [primaryImage, ...secondaryImages], banner: primaryImage })
    }, [lot, getLotImages])

    useEffect(() => {
        fetchImages()
    }, [fetchImages])

    // disable next button on every lot route change
    useEffect(() => {
        setNextDisabled(true)
        setNextButtonLoading(false)
    }, [location.pathname, setNextDisabled])

    const context: ILotContext = useMemo(
        () => ({
            lot,
            nextDisabled,
            nextButtonLoading,
            modifyAndSetLot,
            setLot,
            setNextDisabled,
            setNextButtonLoading
        }),
        [lot, nextDisabled, nextButtonLoading, modifyAndSetLot, setNextDisabled]
    )

    return <LotContextProvider context={context}>{children}</LotContextProvider>
}
